import React from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight, FaExternalLinkAlt } from "react-icons/fa";
import LanguageSwitch from "../components/LanguageSwitch";
import { useLanguage } from "../contexts/LanguageContext";
import logo from "../assets/ourdo-tail-curve-wordmark.svg";
import "../styles.css";

type LocalizedText = { id: string; en: string };

type WorkItem = {
  name: string;
  status: string;
  category: string;
  summary: LocalizedText;
  built: LocalizedText;
  proof: string[];
  href: string;
  featured?: boolean;
  note?: LocalizedText;
  image?: string;
};

const WORK: WorkItem[] = [
  {
    name: "BARUNA",
    status: "LIVE PRODUCT",
    category: "Spatial Intelligence",
    summary: {
      id: "Produk environmental dan spatial intelligence untuk membantu tim memahami area proyek melalui evidence yang terstruktur.",
      en: "An environmental and spatial intelligence product for helping teams understand project areas through structured evidence.",
    },
    built: {
      id: "Project-area workflow, evidence context, visualization, reusable spatial review flow, dan output yang siap direview.",
      en: "Project-area workflow, evidence context, visualization, reusable spatial review flow, and review-ready output.",
    },
    proof: ["Project-area workflow", "Spatial evidence", "Environmental context", "Review-ready output"],
    href: "https://baruna.ourdo.id/",
    image: "/assets/portfolio/baruna-cover.webp",
    featured: true,
  },
  {
    name: "Impact MIS",
    status: "PROPOSAL PROTOTYPE",
    category: "Data & Monitoring Systems",
    summary: {
      id: "Konsep MIS untuk program monitoring, indicator tracking, evidence review, spatial context, dan field submission.",
      en: "An MIS concept for programme monitoring, indicator tracking, evidence review, spatial context, and field submission.",
    },
    built: {
      id: "Management dashboard, program detail, indicator review, landscape explorer, dan responsive field evidence flow.",
      en: "Management dashboard, programme detail, indicator review, landscape explorer, and responsive field evidence flow.",
    },
    proof: ["Programme monitoring", "Indicator tracking", "Evidence review", "Field submission workflow"],
    href: "https://impact-mis.ourdo.id/",
    image: "/assets/portfolio/impact-mis-cover.webp",
    featured: true,
    note: {
      id: "Prototype proposal dengan data demonstrasi fiktif; bukan production MIS.",
      en: "Proposal prototype using fictional demonstration data; not a production MIS.",
    },
  },
  {
    name: "Ourdo Atlas",
    status: "LMS MVP",
    category: "Learning Operations Platform",
    summary: {
      id: "Learning operations platform untuk admin, mentor, dan student dengan area kerja berbasis role dan workflow pembelajaran yang lebih terstruktur.",
      en: "A learning operations platform for admins, mentors, and students with role-based work areas and a more structured learning workflow.",
    },
    built: {
      id: "Role-based learning operations, login flow, admin/mentor/student access, dan fondasi LMS yang bisa dikembangkan sesuai kebutuhan organisasi.",
      en: "Role-based learning operations, login flow, admin/mentor/student access, and an LMS foundation that can be extended for organisational needs.",
    },
    proof: ["Role-based learning operations", "Admin / mentor / student workflow", "Custom LMS foundation", "Learning delivery system"],
    href: "https://atlas.ourdo.id/login",
    image: "/assets/portfolio/ourdo-atlas-cover.webp",
    featured: true,
    note: {
      id: "MVP; diposisikan sebagai fondasi custom learning operations/LMS, bukan SaaS production-proven.",
      en: "MVP; positioned as a custom learning operations/LMS foundation, not a production-proven SaaS product.",
    },
  },
  {
    name: "Our Mist Studio",
    status: "LIVE PRODUCT INITIATIVE",
    category: "Game Studio",
    summary: {
      id: "Studio game misteri interaktif dengan playable web investigation dan produk narrative game yang sedang dikembangkan.",
      en: "An interactive mystery game studio developing playable web investigations and narrative game experiences.",
    },
    built: {
      id: "Product site, playable K Case Files, digital-first narrative UX, evidence interaction, dan product roadmap.",
      en: "Product site, playable K Case Files, digital-first narrative UX, evidence interactions, and a product roadmap.",
    },
    proof: ["Consumer product", "Playable investigation", "Interactive storytelling", "Evidence-driven UX"],
    href: "https://mist.ourdo.id/",
    image: "/assets/portfolio/our-mist-cover.webp",
    featured: true,
  },
  {
    name: "Our Mercury",
    status: "PRODUCT PROTOTYPE",
    category: "Travel Guide Marketplace",
    summary: {
      id: "Platform marketplace untuk traveler, local guide, dan tour guide dengan profile, service area, language, pricing, dan booking-request flow.",
      en: "A marketplace platform for travelers, local guides, and tour guides with discovery, profiles, service areas, pricing, and trip-request flows.",
    },
    built: {
      id: "Discovery traveler, profile guide, service area, pricing, onboarding guide, dan alur request perjalanan.",
      en: "Traveler discovery, guide profiles, service areas, pricing, guide onboarding, and trip-request flows.",
    },
    proof: ["Two-sided marketplace", "Guide discovery", "Profile comparison", "Booking-request journey"],
    href: "https://our-mercury-web.vercel.app/",
    image: "/assets/portfolio/our-mercury-cover.webp",
  },
  {
    name: "Lunarian",
    status: "COMMERCE PROTOTYPE",
    category: "Marketplace Example",
    summary: {
      id: "Contoh marketplace commerce untuk curated sneaker drops, collection browsing, product flow, dan member-oriented experience.",
      en: "A marketplace example for product catalogues, discovery, product detail, checkout, and membership flows.",
    },
    built: {
      id: "Homepage marketplace, katalog, product discovery, product detail, checkout path, dan membership flow.",
      en: "Marketplace homepage, catalogue, product discovery, product detail, checkout path, and membership flow.",
    },
    proof: ["Commerce journey", "Product discovery", "Curated catalogue", "Checkout & membership concept"],
    href: "https://lunarian-ten.vercel.app/",
    image: "/assets/portfolio/lunarian-cover.webp",
    note: {
      id: "Prototype; metric commerce yang tampil di demo tidak diperlakukan sebagai business metrics nyata.",
      en: "Prototype; commerce metrics shown in the demo are not presented as real business metrics.",
    },
  },
];

const learning = {
  privateOfferings: 4,
  buddySessions: 1,
  tracks: 3,
  privateTitles: ["Private Backend", "Private Frontend", "Private Anak SD", "Private Anak SMP"],
  trackTitles: ["Learning Experience Design", "Digital Facilitation", "Community Upskilling"],
};

const WorkPage: React.FC = () => {
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const t = (text: LocalizedText) => (isEnglish ? text.en : text.id);

  const featured = WORK.filter((item) => item.featured);

  return (
    <div className="ourdo-site">
      <header className="ourdo-header">
        <div className="ourdo-container ourdo-nav">
          <Link to="/" className="ourdo-brand">
            <img src={logo} alt="OURDO Indonesia — Tail Curve" />
            <div>
              <strong>INDONESIA</strong>
              <span>{isEnglish ? "Selected work & product proof" : "Selected work & product proof"}</span>
            </div>
          </Link>

          <div className="ourdo-nav-links">
            <Link to="/">{isEnglish ? "Home" : "Beranda"}</Link>
            <a href="#all-work">{isEnglish ? "All work" : "Semua work"}</a>
            <a href="#learning">{isEnglish ? "Learning" : "Learning"}</a>
          </div>

          <div className="ourdo-nav-actions">
            <LanguageSwitch />
            <Link className="ourdo-btn ourdo-btn-secondary" to="/">
              <FaArrowLeft /> {isEnglish ? "Back home" : "Kembali"}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="ourdo-container ourdo-work-hero">
          <p className="ourdo-eyebrow">{isEnglish ? "OUR WORK" : "OUR WORK"}</p>
          <h1>{isEnglish ? "Products, systems, prototypes, and delivery proof." : "Produk, sistem, prototype, dan proof delivery."}</h1>
          <p>
            {isEnglish
              ? "Ourdo Indonesia works across digital products, data and monitoring systems, spatial intelligence, and learning. We label each project by its real maturity so prototypes are not presented as production systems."
              : "Ourdo Indonesia bekerja di digital products, data dan monitoring systems, spatial intelligence, dan learning. Setiap project diberi status maturity yang sebenarnya agar prototype tidak terlihat seperti production system."}
          </p>
        </section>

        <section className="ourdo-section">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "FEATURED WORK" : "FEATURED WORK"}</p>
              <h2>{isEnglish ? "A small set of proof across different capabilities." : "Proof utama dari beberapa capability yang berbeda."}</h2>
            </div>

            <div className="ourdo-work-feature-grid">
              {featured.map((item, index) => (
                <article className="ourdo-work-feature" key={item.name}>
                  <div className="ourdo-work-number">0{index + 1}</div>
                  <div>
                    {item.image && (
                      <div className="ourdo-work-feature-media">
                        <img src={item.image} alt={`${item.name} product interface`} loading="lazy" />
                      </div>
                    )}
                    <div className="ourdo-work-meta">
                      <span className="ourdo-status">{item.status}</span>
                      <span>{item.category}</span>
                    </div>
                    <h3>{item.name}</h3>
                    <p>{t(item.summary)}</p>
                    <div className="ourdo-work-built">
                      <span>{isEnglish ? "What was built" : "Yang dibangun"}</span>
                      <strong>{t(item.built)}</strong>
                    </div>
                    <div className="ourdo-tags">
                      {item.proof.map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                    {item.note && <p className="ourdo-work-note">{t(item.note)}</p>}
                    <a className="ourdo-text-link" href={item.href} target="_blank" rel="noreferrer">
                      {isEnglish ? "Open project" : "Buka project"} <FaExternalLinkAlt />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="digital-products">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "DIGITAL PRODUCTS & PLATFORMS" : "DIGITAL PRODUCTS & PLATFORMS"}</p>
              <h2>{isEnglish ? "Different products for different user journeys." : "Produk yang berbeda untuk user journey yang berbeda."}</h2>
              <p>{isEnglish ? "These projects show product thinking across learning operations, marketplaces, travel, commerce, and interactive games—not a list of technologies." : "Project-project ini menunjukkan product thinking di learning operations, marketplace, travel, commerce, dan game interaktif—bukan daftar teknologi."}</p>
            </div>

            <div className="ourdo-grid-3">
              {WORK.filter((item) => ["Learning Operations Platform", "Game Studio", "Travel Guide Marketplace", "Marketplace Example"].includes(item.category)).map((item) => (
                <article className="ourdo-work-card" key={item.name}>
                  {item.image && (
                    <div className="ourdo-work-card-media">
                      <img src={item.image} alt={`${item.name} product interface`} loading="lazy" />
                    </div>
                  )}
                  <div className="ourdo-work-meta">
                    <span className="ourdo-status">{item.status}</span>
                    <span>{item.category}</span>
                  </div>
                  <h3>{item.name}</h3>
                  <p>{t(item.summary)}</p>
                  <div className="ourdo-tags">
                    {item.proof.map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                  <a href={item.href} target="_blank" rel="noreferrer">
                    {isEnglish ? "Open project" : "Buka project"} →
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="all-work">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "ALL WORK" : "ALL WORK"}</p>
              <h2>{isEnglish ? "More product and platform proof." : "Proof produk dan platform lainnya."}</h2>
            </div>

            <div className="ourdo-grid-3">
              {WORK.filter((item) => !item.featured).map((item) => (
                <article className="ourdo-work-card" key={item.name}>
                  {item.image && (
                    <div className="ourdo-work-card-media">
                      <img src={item.image} alt={`${item.name} product interface`} loading="lazy" />
                    </div>
                  )}
                  <div className="ourdo-work-meta">
                    <span className="ourdo-status">{item.status}</span>
                    <span>{item.category}</span>
                  </div>
                  <h3>{item.name}</h3>
                  <p>{t(item.summary)}</p>
                  <div className="ourdo-tags">
                    {item.proof.map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                  {item.note && <p className="ourdo-work-note">{t(item.note)}</p>}
                  <a href={item.href} target="_blank" rel="noreferrer">
                    {isEnglish ? "Open project" : "Buka project"} →
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="learning">
          <div className="ourdo-container ourdo-learning-proof">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "LEARNING & CAPACITY BUILDING" : "LEARNING & CAPACITY BUILDING"}</p>
              <h2>{isEnglish ? "Learning is an established supporting capability." : "Learning adalah supporting capability yang sudah punya offering nyata."}</h2>
              <p>
                {isEnglish
                  ? "The current codebase supports concrete learning offerings. These counts describe what is currently defined in the product repository—not historical delivery totals."
                  : "Codebase saat ini sudah punya offering learning yang konkret. Angka berikut adalah jumlah offering yang terdefinisi di repository, bukan total historis delivery."}
              </p>
            </div>

            <div className="ourdo-learning-stats">
              <article><strong>{learning.privateOfferings}</strong><span>{isEnglish ? "Private class offerings" : "Private class offerings"}</span></article>
              <article><strong>{learning.buddySessions}</strong><span>{isEnglish ? "1-on-1 technical session product" : "Produk sesi teknis 1-on-1"}</span></article>
              <article><strong>{learning.tracks}</strong><span>{isEnglish ? "Learning programme tracks" : "Learning programme tracks"}</span></article>
            </div>

            <div className="ourdo-learning-detail">
              <div>
                <h3>{isEnglish ? "Current private-class catalogue" : "Katalog private class saat ini"}</h3>
                <ul>{learning.privateTitles.map((title) => <li key={title}>{title}</li>)}</ul>
              </div>
              <div>
                <h3>{isEnglish ? "Programme tracks" : "Programme tracks"}</h3>
                <ul>{learning.trackTitles.map((title) => <li key={title}>{title}</li>)}</ul>
              </div>
            </div>

            <Link className="ourdo-btn ourdo-btn-secondary" to="/learning">
              {isEnglish ? "Explore Learning" : "Explore Learning"} <FaArrowRight />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WorkPage;
